// src/modules/app-views/landingPages/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { FormOutlined, CloudUploadOutlined, BarChartOutlined, RobotOutlined, GlobalOutlined } from '@ant-design/icons';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import translateX from 'utils/translateX';
import { generateUidV4 } from 'utils/helpers';
import RecordTab from './tabs/BuilderTab';
import WebsiteIaTab from './tabs/WebsiteIaTab';
import PublicationTab from './tabs/PublicationTab';
import MetricsTab from './tabs/MetricsTab';
import AiConsumptionTab from './tabs/AiConsumptionTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LandingPagesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['name'];

  const tabsItems = useMemo(
    () =>
      Array.isArray(availableTabs)
        ? availableTabs
        : availableTabs
          ? Object.values(availableTabs)
          : [],
    [availableTabs]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(
    () => ({
      name: data?.name || '',
      token: data?.token || generateUidV4(),
      url: data?.url || '',
      pageType: data?.pageType || 'exists',
      htmlContent: data?.htmlContent || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?._id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Build tabs
  useEffect(() => {
    const childProps = { ...props, initTab, viewMode, data };
    buildTabs(buildTabsArray(childProps));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initTab, viewMode]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const lpId = values?._id || allValues?._id || data?._id;
      const dataToSave = {
        ...values,
        // Include htmlContent from form state (set via form.setFieldValue by AI panel)
        htmlContent: allValues?.htmlContent ?? data?.htmlContent ?? '',
      };

      // Remove offerId — relationship is inverted (Offer references LPs, not the other way)
      delete dataToSave.offerId;

      // URL is only relevant for existing pages; clear it for AI-created pages
      if (dataToSave.pageType === 'create') {
        dataToSave.url = '';
      }

      await upsert({ dataSet: dataToSave, _id: lpId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LandingPagesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode } = props;

  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <RecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'website_ia',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <GlobalOutlined />
          {translateX('website_ia')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <WebsiteIaTab {...props} tabName="website_ia" />,
    },
    {
      key: 'publication',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CloudUploadOutlined />
          {translateX('publication')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <PublicationTab {...props} tabName="publication" />,
    },
    {
      key: 'metrics',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChartOutlined />
          {translateX('metrics')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <MetricsTab {...props} tabName="metrics" />,
    },
    {
      key: 'ai_consumption',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RobotOutlined />
          {translateX('ai_consumption')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: <AiConsumptionTab {...props} tabName="ai_consumption" />,
    },
  ];
}
